import type { Enums, Tables } from '../../db_types'

/** Inputs shared by HTTP callers and future agent capabilities. */
export type MetricPreviewScope = {
  organizationId: number
  metricId: number
  month: string
  creditSetupId?: number
  teamIds: number[]
  ruleIds?: number[]
}

export type MetricPreviewPage = {
  offset: number
  limit: number
}

export type MetricPreviewSummaryRequest = MetricPreviewScope &
  MetricPreviewPage & {
    profileIds?: string[]
    search?: string
    sortBy?: 'user' | 'total' | 'includedCount'
    sortDescending?: boolean
  }

export type MetricPreviewRecordsRequest = MetricPreviewScope &
  MetricPreviewPage & {
    profileId: string
    included: boolean
    search?: string
  }

export type MetricPreviewAppliedScope = Required<
  Pick<MetricPreviewScope, 'organizationId' | 'metricId' | 'month'>
> & {
  creditSetupId: number | null
  teamIds: number[]
  ruleIds: number[]
}

export type MetricPreviewReasonCode =
  | 'included'
  | 'metric_filter'
  | 'invalid_accrual_date'
  | 'outside_month'
  | 'outside_team_scope'
  | 'outside_profile_scope'
  | 'no_credit_assignment'
  | 'rule_filter'
  | 'unselected_rule'

export type MetricPreviewRecord = {
  attributionKey: string
  source: Enums<'plan_source'>
  recordId: number
  profileId: string | null
  teamId: number | null
  accrualDate: string | null
  included: boolean
  reasonCode: MetricPreviewReasonCode
  contribution: number | null
  creditSetupRuleId: number | null
  recordLabel: string | null
  dealName?: string | null
  dealExternalId?: string | null
  fields: Record<string, string | number | boolean | string[] | null>
}

export type MetricPreviewUser = {
  profileId: string
  name: string | null
  email: string | null
  imgUrl?: string | null
  total: number
  includedCount: number | null
}

export type MetricPreviewResultStatus = 'ready' | 'unavailable'

/** Live, best-effort work counts for an asynchronous preview run. */
export type MetricPreviewProgress = {
  phase: 'preparing' | 'calculating' | 'finalizing'
  completedRecipients: number
  totalRecipients: number | null
  scannedRecords: number
}

export type MetricPreviewResultBase = {
  version: 1
  calculatedAt: string
  scope: MetricPreviewAppliedScope
  metricType: Tables<'metrics'>['type']
  unit: 'currency' | 'percentage' | 'count' | 'number'
  aggregateFunction: Tables<'metrics'>['aggregate_function']
  currency: string | null
  workLimits?: {
    maxSourceCandidates: number
    maxCalculationMs: number
  }
  status: MetricPreviewResultStatus
  unavailableReason?: 'source_less_metric'
}

export type MetricPreviewSummaryPage = MetricPreviewResultBase & {
  users: MetricPreviewUser[]
  totalUsers: number
  offset: number
  limit: number
}

export type MetricPreviewRecordsPage = MetricPreviewResultBase & {
  profileId: string
  included: boolean
  records: MetricPreviewRecord[]
  totalRecords: number
  offset: number
  limit: number
}

export type MetricPreviewErrorCode =
  | 'invalid_scope'
  | 'not_found'
  | 'forbidden'
  | 'too_broad'
  | 'source_failure'

export class MetricPreviewError extends Error {
  constructor(
    public readonly code: MetricPreviewErrorCode,
    message: string
  ) {
    super(message)
    this.name = 'MetricPreviewError'
  }
}

export const METRIC_PREVIEW_MAX_USER_PAGE_SIZE = 50
export const METRIC_PREVIEW_MAX_RECORD_PAGE_SIZE = 50
